import { useEffect, useState, useRef, lazy, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import { useToast } from '../ui/Toast'
import { useAuthStore } from '../../store/auth'
import { Button } from '../ui/Button'
import type { SitePlan } from '@shared/types'

const PdfPageSelector = lazy(() => import('../floorplan/PdfPageSelector'))

const DELETE_ROLES = ['super_admin', 'admin', 'manager', 'technician']

/** Thumbnail for plan backgrounds — always rendered as <img> since PDFs are converted to PNG at upload */
function PlanThumbnail({ r2Key }: { r2Key: string }) {
  return <img src={`/api/files/${r2Key}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
}

interface Props {
  siteId: string
}

export function SiteFloorplansTab({ siteId }: Props) {
  const [plans, setPlans] = useState<SitePlan[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddPlan, setShowAddPlan] = useState(false)
  const [addPlanForm, setAddPlanForm] = useState({ name: '', floor_number: 0, source_type: 'blank' as 'pdf' | 'image' | 'satellite' | 'blank' })
  const [addPlanFile, setAddPlanFile] = useState<File | null>(null)
  const [addPlanSaving, setAddPlanSaving] = useState(false)
  const [deletingSlug, setDeletingSlug] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<SitePlan | null>(null)
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [pendingPlanSlug, setPendingPlanSlug] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { toast, toastError } = useToast()
  const userRole = useAuthStore(s => s.user?.role)
  const canDelete = DELETE_ROLES.includes(userRole || '')

  useEffect(() => {
    api.get<SitePlan[]>(`/sites/${siteId}/plans`)
      .then(setPlans)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId])

  async function handleDelete(plan: SitePlan) {
    setDeletingSlug(plan.slug)
    try {
      await api.delete(`/plans/${plan.slug}`)
      setPlans(prev => prev.filter(p => p.id !== plan.id))
      toast('Floor plan deleted')
    } catch (err) {
      toastError('Failed to delete floor plan', err)
    } finally {
      setDeletingSlug(null)
      setConfirmDelete(null)
    }
  }

  const needsFile = addPlanForm.source_type === 'pdf' || addPlanForm.source_type === 'image'
  const fileAccept = addPlanForm.source_type === 'pdf'
    ? '.pdf,application/pdf'
    : '.png,.jpg,.jpeg,image/*'

  if (loading) {
    return (
      <div>
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Floorplans</h3>
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton" style={{ height: 200, borderRadius: 'var(--radius-xl)' }} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Floorplans</h3>
      <div className="grid grid-cols-3 gap-4">
        {plans.map(plan => (
          <div
            key={plan.id}
            className="card"
            style={{ padding: 0, overflow: 'hidden', cursor: 'pointer', transition: 'box-shadow 0.15s', position: 'relative' }}
            onClick={() => navigate(`/site-plans/${plan.slug}`)}
            onMouseEnter={e => (e.currentTarget.style.boxShadow = 'var(--shadow-md)')}
            onMouseLeave={e => (e.currentTarget.style.boxShadow = 'var(--shadow-xs)')}
          >
            {canDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(plan) }}
                title="Delete floor plan"
                style={{
                  position: 'absolute', top: 8, right: 8, zIndex: 2,
                  width: 28, height: 28, borderRadius: 6,
                  background: 'rgba(0,0,0,0.5)', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-tertiary)', transition: 'color 0.15s, background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.background = 'rgba(0,0,0,0.7)' }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-tertiary)'; e.currentTarget.style.background = 'rgba(0,0,0,0.5)' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" /><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                  <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                </svg>
              </button>
            )}
            <div style={{
              height: 140, background: 'var(--bg-inset)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 32, color: 'var(--text-tertiary)',
            }}>
              {plan.background_r2_key
                ? <PlanThumbnail r2Key={plan.background_r2_key} />
                : '\uD83D\uDCD0'}
            </div>
            <div style={{ padding: '12px 16px' }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{plan.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                Floor {plan.floor_number} &middot; {plan.source_type}
              </div>
            </div>
          </div>
        ))}

        {/* Add new floorplan card */}
        <div
          style={{
            border: '2px dashed var(--border)', borderRadius: 'var(--radius-xl)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', gap: 8, minHeight: 200, cursor: 'pointer',
            transition: 'border-color 0.15s',
          }}
          onClick={() => setShowAddPlan(true)}
          onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--primary)')}
          onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
        >
          <div style={{ fontSize: 24, color: 'var(--primary)' }}>+</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)' }}>Add Floorplan</div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>PDF, Image, Satellite, or Blank</div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={() => setConfirmDelete(null)} />
          <div style={{ position: 'relative', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl, 12px)', padding: 24, width: 400, boxShadow: 'var(--shadow-xl)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Delete Floor Plan</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              Delete &ldquo;{confirmDelete.name}&rdquo;? This cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => setConfirmDelete(null)} disabled={!!deletingSlug}>Cancel</Button>
              <Button
                variant="danger"
                onClick={() => handleDelete(confirmDelete)}
                loading={deletingSlug === confirmDelete.slug}
                disabled={!!deletingSlug}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Floorplan Modal */}
      {showAddPlan && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={() => setShowAddPlan(false)} />
          <div style={{ position: 'relative', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl, 12px)', padding: 24, width: 420, boxShadow: 'var(--shadow-xl)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>New Floorplan</h3>
            <form onSubmit={async (e) => {
              e.preventDefault()
              setAddPlanSaving(true)
              try {
                // Create the plan first
                const plan = await api.post<SitePlan>(`/sites/${siteId}/plans`, addPlanForm)

                // If a PDF file was selected, show the page selector instead of uploading directly
                const isPdf = addPlanFile && (addPlanFile.type === 'application/pdf' || addPlanFile.name.toLowerCase().endsWith('.pdf'))
                if (isPdf && plan.slug) {
                  setPendingPlanSlug(plan.slug)
                  setPdfFile(addPlanFile)
                  setShowAddPlan(false)
                  setAddPlanSaving(false)
                  return
                }

                // Non-PDF: upload directly as before
                if (addPlanFile && plan.slug) {
                  const formData = new FormData()
                  formData.append('file', addPlanFile)
                  await api.upload(`/plans/${plan.slug}/background`, formData)
                }

                setShowAddPlan(false)
                setAddPlanFile(null)
                navigate(`/site-plans/${plan.slug}`)
              } catch { toast('Failed to create floorplan', 'error') }
              finally { setAddPlanSaving(false) }
            }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Name *</label>
                <input className="v3-input" required value={addPlanForm.name} onChange={e => setAddPlanForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Ground Floor" />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Floor Number</label>
                <input className="v3-input" type="number" value={addPlanForm.floor_number} onChange={e => setAddPlanForm(f => ({ ...f, floor_number: parseInt(e.target.value) || 0 }))} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Source Type</label>
                <div className="v3-select-wrap">
                  <select className="v3-select" value={addPlanForm.source_type} onChange={e => {
                    setAddPlanForm(f => ({ ...f, source_type: e.target.value as typeof f.source_type }))
                    setAddPlanFile(null)
                    if (fileInputRef.current) fileInputRef.current.value = ''
                  }}>
                    <option value="blank">Blank Canvas</option>
                    <option value="image">Image Upload</option>
                    <option value="pdf">PDF Upload</option>
                    <option value="satellite">Satellite View</option>
                  </select>
                  <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
                </div>
              </div>

              {/* File input for PDF/Image source types */}
              {needsFile && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                    {addPlanForm.source_type === 'pdf' ? 'PDF File' : 'Image File'}
                  </label>
                  <input
                    ref={fileInputRef}
                    className="v3-input"
                    type="file"
                    accept={fileAccept}
                    onChange={e => setAddPlanFile(e.target.files?.[0] || null)}
                    style={{ padding: '6px 8px' }}
                  />
                  {!addPlanFile && (
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                      You can also upload later from the designer
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="secondary" onClick={() => { setShowAddPlan(false); setAddPlanFile(null) }}>Cancel</Button>
                <Button type="submit" loading={addPlanSaving}>Create Floorplan</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PDF Page Selector Modal */}
      {pdfFile && pendingPlanSlug && (
        <Suspense fallback={null}>
          <PdfPageSelector
            file={pdfFile}
            onCancel={() => {
              setPdfFile(null)
              // Navigate to the plan anyway (they can upload later)
              navigate(`/site-plans/${pendingPlanSlug}`)
              setPendingPlanSlug(null)
              setAddPlanFile(null)
            }}
            onSelect={async (pngBlob, _pageNumber, width, height) => {
              try {
                const pngFile = new File([pngBlob], 'floor-plan.png', { type: 'image/png' })
                const formData = new FormData()
                formData.append('file', pngFile)
                await api.upload(`/plans/${pendingPlanSlug}/background`, formData)
                // Update dimensions
                await api.patch(`/plans/${pendingPlanSlug}`, { width_px: width, height_px: height }).catch(() => {})
                toast('Floor plan uploaded')
              } catch (err) {
                toastError('Failed to upload floor plan', err)
              }
              setPdfFile(null)
              setAddPlanFile(null)
              navigate(`/site-plans/${pendingPlanSlug}`)
              setPendingPlanSlug(null)
            }}
          />
        </Suspense>
      )}
    </div>
  )
}
